import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import api from '../utils/requests/base';
import { usersAtom } from '../utils/atoms';
import { formatPhoneNumber } from '../utils/phoneNumber';
import LetterAvatar from './child/LetterAvatar';

const UsersListLayer = () => {
    const [users, setUsers] = useAtom(usersAtom);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('פעיל');
    const navigate = useNavigate();

    // Fetch students data from API
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoading(true);
                const response = await api.get('/teacher/students');
                const studentsData = response.data;

                // Map the API response to our component format
                const mappedUsers = studentsData.map(student => ({
                    id: student.id,
                    uid: student.uid,
                    name: `${student.firstName} ${student.lastName}`,
                    firstName: student.firstName,
                    lastName: student.lastName,
                    email: student.email || 'לא הוגדר',
                    phoneNumber: student.phoneNumber,
                    joinDate: new Date(student.firstLessonDate).toLocaleDateString('en-GB'),
                    department: student.studyAreaHebrew || 'לא הוגדר',
                    designation: 'תלמיד',
                    status: student.active ? 'פעיל' : 'לא פעיל',
                    avatar: student.idPhotoUrl || "assets/images/user-list/user-list1.png",
                    idNumber: student.idNumber,
                    dateOfBirth: student.dateOfBirth,
                    lessons: student.lessons,
                    lessonsOffset: student.lessonsOffset,
                    theory: student.theory,
                    passedTest: student.passedTest,
                    greenForm: student.greenForm,
                    glasses: student.glasses,
                    canGetSMS: student.canGetSMS,
                    canGetPush: student.canGetPush,
                    canGetReminders: student.canGetReminders,
                    specialPrice: student.specialPrice,
                    paymentsBalance: student.paymentsBalance,
                    platform: student.platform,
                    appVersion: student.appVersion,
                    note: student.note
                }));

                setUsers(mappedUsers);
            } catch (error) {
                console.error('Error fetching students:', error);
                setUsers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [setUsers]);

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.phoneNumber?.includes(searchTerm);
        const matchesStatus = !statusFilter || user.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const handleViewUser = (userId) => {
        navigate(`/view-profile/${userId}`);
    };

    const handleEditUser = (userId) => {
        // TODO: Implement edit user functionality
        console.log('Edit user:', userId);
    };

    const handleDeleteUser = (userId) => {
        // TODO: Implement delete user functionality
        console.log('Delete user:', userId);
    };

    if (loading) {
        return (
            <div className="card h-100 p-0 radius-12">
                <div className="card-body p-24 d-flex justify-content-center align-items-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <form className="navbar-search">
                        <input
                            type="text"
                            className="bg-base h-40-px w-auto"
                            name="search"
                            placeholder="חפש לפי שם, מספר נייד וכו׳"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Icon icon="ion:search-outline" className="icon" />
                    </form>
                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px text-end"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">כל הסטטוסים</option>
                        <option value="פעיל">פעילים</option>
                        <option value="לא פעיל">לא פעילים</option>
                    </select>
                </div>
                <Link
                    to="/add-user"
                    className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                >
                    <Icon
                        icon="ic:baseline-plus"
                        className="icon text-xl line-height-1"
                    />
                    הוספת תלמיד חדש
                </Link>
            </div>
            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col"></th>
                                <th scope="col">שם</th>
                                <th scope="col">מספר שיעורים</th>
                                <th scope="col">טלפון</th>
                                <th scope="col" className="text-center">
                                    יתרה
                                </th>
                                <th scope="col" className="text-center">
                                    סטטוס
                                </th>
                                <th scope="col" className="text-center">
                                    פעולות
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-24">
                                        <div className="d-flex flex-column align-items-center gap-2">
                                            <Icon icon="mdi:account-group-outline" className="text-xl text-neutral-400" />
                                            <span className="text-neutral-500">No students found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map((user, index) => (
                                    <tr key={user.id}>
                                        <td>
                                            <LetterAvatar
                                                letter={user.name}
                                                size="40px"
                                                className="flex-shrink-0 me-12"
                                            />
                                        </td>
                                        <td>
                                            <span className="text-md mb-0 fw-normal text-secondary-light">
                                                {user.name}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <span className="text-md mb-0 fw-normal text-secondary-light">
                                                {user.lessons}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="text-md mb-0 fw-normal text-secondary-light">
                                                {formatPhoneNumber(user.phoneNumber)}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <span className={`fw-medium text-sm ${user.paymentsBalance < 0
                                                ? 'text-danger-600'
                                                : user.paymentsBalance > 0
                                                    ? 'text-success-600'
                                                    : 'text-secondary-light'
                                                }`}>
                                                ₪{user.paymentsBalance}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <span className={`px-24 py-4 radius-4 fw-medium text-sm ${user.status === 'פעיל'
                                                ? 'bg-success-focus text-success-600 border border-success-main'
                                                : 'bg-neutral-200 text-neutral-600 border border-neutral-400'
                                                }`}>
                                                {user.status}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex align-items-center gap-10 justify-content-center">
                                                <button
                                                    type="button"
                                                    className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    onClick={() => handleViewUser(user.id)}
                                                    title="View Student"
                                                >
                                                    <Icon
                                                        icon="majesticons:eye-line"
                                                        className="icon text-xl"
                                                    />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    onClick={() => handleEditUser(user.id)}
                                                    title="Edit Student"
                                                >
                                                    <Icon icon="lucide:edit" className="menu-icon" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    onClick={() => handleDeleteUser(user.id)}
                                                    title="Delete Student"
                                                >
                                                    <Icon
                                                        icon="fluent:delete-24-regular"
                                                        className="menu-icon"
                                                    />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default UsersListLayer;